import { FC, Fragment, memo, useEffect, useRef, useState } from "react"
import { Button } from "../Button"
import { Step } from "./Step"

import { copyToClipboard, ExClient, selectUser, useSetupOTP, useStoreClient } from "@usher/pe-client-front-end-lib"
import { clsx } from "clsx"
import { Copy } from "lucide-react"
import { useCallback } from "react"
import { FormProvider } from "react-hook-form"
import { useHistory } from "react-router-dom"
import { toast } from "react-toastify"
import { ContentLoading } from "../content-loading"
import { FormControl } from "../Form/FormControl"
import { OTPInput } from "../Form/OPTInput"
import { Modal } from "../Modal"
import { CopyIcon } from "./CopyIcon"
import { DownloadIcon } from "./DownloadIcon"
import "./style.sass"

const TwoFactorRegisterComponent: FC = () => {
  const history = useHistory()
  const [showModal, setShowModal] = useState(false)
  const block = useRef(true)
  const { totp_enabled } = useStoreClient(selectUser)
  const unblock = history.block(() => {
    if (totp_enabled) return true as unknown as void
    return !block.current as unknown as void
  })

  const { loading, codesChunk, data, enableTotp, enableLoading, formMethods } = useSetupOTP(totp_enabled, () => {
    setShowModal(true)
  })
  const closeModal = () => {
    removeEvents()
    block.current = false
    ExClient.logout()
  }
  const onleave = useCallback((e: FocusEvent) => {
    e.preventDefault()
  }, [])

  const onUnload = useCallback((e: BeforeUnloadEvent) => {
    e.preventDefault()
  }, [])

  const removeEvents = () => {
    window.removeEventListener("blur", onleave)
    window.removeEventListener("beforeunload", onUnload)
  }
  useEffect(() => {
    if (totp_enabled) {
      // history.replace("/settings/two-factor")
      removeEvents()
      block.current = false
      unblock()
    } else {
      window.addEventListener("blur", onleave)
      window.addEventListener("beforeunload", onUnload)
    }
    return () => {
      unblock()
      removeEvents()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totp_enabled])
  return (
    <Fragment>
      <ContentLoading loading={loading}>
        <div className={clsx("two-factor-register", loading && "content-loading")}>
          <Step step={1} title="Instal app for Two - Factor Authentication" />
          <span className="text-1">Get Authentication app:</span>
          <div className="actions">
            <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noreferrer">
              <img alt="google-play" src="/client/img/google-play.png" className="store-button" />
            </a>
            <a href="https://apps.apple.com/app/google-authenticator/id388497605" target="_blank" rel="noreferrer">
              <img alt="google-play" src="/client/img/app-store.png" className="store-button" />
            </a>
          </div>

          <div className="divider" />

          <Step step={2} title="Scan this QR code or use your setup KEY" />

          <span className="text-1 max-w-[520px]">
            Open your authentication app, tap the “+” icon in the bottom corner of the screen, and use your phone’s camera to scan the QR code.
          </span>

          <img alt="qr-code" src={data?.qr_png_base64} className="border w-[260px] aspect-square" />
          {data?.secret_base32 && (
            <>
              <div className="mt-3">
                <span className="text-1">
                  You can use your setup key instead of a QR code. <br />
                  Open the app, tap the “+” icon at the bottom of the screen, and select “Enter a setup key.”
                </span>
                <p className="text-[16px] font-bold mt-2 break-words leading-7">
                  {data?.secret_base32}
                  <span
                    className="p-2  md:hover:bg-main-main ml-2 cursor-pointer text-inherit hover:text-white rounded-lg inline-block align-middle -mt-1"
                    onClick={() => {
                      copyToClipboard(String(data?.secret_base32))
                    }}
                  >
                    <Copy size={17} />
                  </span>
                </p>
              </div>
            </>
          )}
          <div className="divider" />

          <Step
            step={3}
            title={
              <span className="inline-block" style={{ maxWidth: 424 }}>
                Enter your Two-Factor Authentication (2FA) code in the field below, then click the Verify Code button.
              </span>
            }
          />

          <span className="text-2">Specify your Two-Factor Authentication code in the field below, after than press the Verify code button</span>

          <div className="actions">
            {/* @ts-ignore */}
            <FormProvider {...formMethods}>
              <form noValidate autoComplete="off" onSubmit={formMethods.handleSubmit(enableTotp)} className="w-full sm:w-fit">
                <div className="bg-tbGray flex flex-col sm:flex-row items-start gap-2.5">
                  <FormControl
                    name="code"
                    required
                    rules={{
                      pattern: { value: /^\d{3}-\d{3}$/g, message: "Invalid OTP format. Use XXX-XXX" },
                    }}
                  >
                    <OTPInput placeholder="___-___" className="w-full sm:w-[400px]" />
                  </FormControl>
                  <Button className="w-full" variant="contained" type="submit" loading={enableLoading}>
                    Verify Code
                  </Button>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      </ContentLoading>
      <Modal show={showModal} onClose={closeModal} title="Make a copy of backup codes" className="p-4 md:!p-8">
        <div className="two-factor max-w-[600px] p-0 pt-4">
          <span className="text-6 !leading-normal">
            Recovery codes can be used to log in to your account if your device is lost, stolen, or unavailable. These codes are displayed only once,
            so you should make a copy before proceeding. We recommend storing them securely, offline, and separately from your login credentials.
          </span>
          <div className="codes-top-row flex gap-3">
            <span className="secret-key-title">Secret Key</span>
            <span className="secret-key-text">{data?.secret_base32}</span>
          </div>
          <div className="divider" />
          {codesChunk?.map((codes) => (
            <div className="codes-row">
              <span className="code-text">{codes[0]}</span>
              <span className="code-text text-center">{codes[1]}</span>
              <span className="code-text text-right">{codes[2]}</span>
            </div>
          ))}
          <div className="flex gap-2.5 mt-6 *:flex-1 flex-wrap">
            <Button
              leftIcon={<CopyIcon />}
              onClick={() => {
                navigator.clipboard.writeText(`Secret Key: ${data.secret_base32} \n${codesChunk.flat().join("\n")}`)
                toast.success("Backup codes copied to clipboard")
              }}
            >
              Copy
            </Button>
            <Button
              leftIcon={<DownloadIcon />}
              onClick={() => {
                const element = document.createElement("a")
                element.setAttribute(
                  "href",
                  "data:text/plain;charset=utf-8," +
                    encodeURIComponent(`Secret Key: ${data.secret_base32}\n\n${codesChunk.map((chunk) => chunk.join("\n")).join("\n\n")}`),
                )
                element.setAttribute("download", "codes.txt")
                element.style.display = "none"
                document.body.appendChild(element)
                element.click()
                document.body.removeChild(element)
              }}
            >
              Download
            </Button>
          </div>
          <Button variant="outlined" className="w-fit mt-4" onClick={closeModal}>
            Close
          </Button>
        </div>
      </Modal>
    </Fragment>
  )
}

export const TwoFactorRegister = memo(TwoFactorRegisterComponent)
